import React, { useState } from "react";

import { RenderUserRole, UIRender } from "../../utils/CommonGroupingItem";

/** Status dropdown options for report create/edit forms. */
export const REPORT_STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 0, label: "Draft" },
];

/**
 * Renders a styled report category badge.
 * @param {string|number} categoryValue - Category id or label from the API.
 * @param {boolean} [origin=true] - Whether to render with origin styling.
 * @returns {Object} UI render object with text and html.
 */
export const RenderReportCategory = (categoryValue, origin = true) => {
  const normalizedValue = categoryValue?.toString().toLowerCase() || "";

  switch (normalizedValue) {
    case "1":
    case "registrations":
      return UIRender({
        color: "cat-green",
        text: "Registrations",
        type: "brandList",
        origin,
      });

    case "2":
    case "students":
      return UIRender({
        color: "cat-orange",
        text: "Students",
        type: "brandList",
        origin,
      });

    case "3":
    case "courses":
      return UIRender({
        color: "cat-purple",
        text: "Courses",
        type: "brandList",
        origin,
      });

    case "4":
    case "assessments":
      return UIRender({
        color: "cat-blue",
        text: "Assessments",
        type: "brandList",
        origin,
      });

    case "5":
    case "finance":
      return UIRender({
        color: "cat-red",
        text: "Finance",
        type: "brandList",
        origin,
      });

    case "6":
    case "other":
      return UIRender({
        color: "cat-grey",
        text: "Other",
        type: "brandList",
        origin,
      });

    default:
      return UIRender({
        color: "cat-blue",
        text: categoryValue || "-",
        type: "brandList",
        origin,
      });
  }
};

/** Field mapping used by `UserColumn` for report user stacks. */
export const REPORT_USER_COLUMN_FIELDS = {
  firstName: "Firstname",
  lastName: "Lastname",
  imgUrl: "picture_me",
  email: "Email",
  mobile: "Mobile",
  userId: "UserID",
  userActiveStatus: "Status",
  right: false,
};

/**
 * Maps report user API items to the shape expected by `UserColumn`.
 * @param {Array<Object>} users - Users from the API.
 * @returns {Array<Object>} User objects for `UserColumn`.
 */
export const mapReportUsersForColumn = (users = []) => {
  if (!Array.isArray(users)) {
    return [];
  }

  return users.map((user) => {
    const nameParts = (user.label || "").replace(/ +/g, " ").trim().split(" ");

    return {
      Firstname: user.Firstname || nameParts[0] || user.label || "NA",
      Lastname: user.Lastname || nameParts.slice(1).join(" "),
      picture_me: user.picture_me || user.profile_pic || "",
      Email: user.Email || user.email || "",
      Mobile: user.Mobile || user.mobile || "",
      UserID: user.user_id ?? user.value,
      Status: user.Status || user.activity_status || "Offline",
    };
  });
};

/**
 * Renders assigned roles using the same styled dots as user management.
 * Shows up to 2 roles by default; expands with See More when there are more.
 * @param {Object} props
 * @param {Array<{ value: number, label: string }>} props.roles - Roles from the API.
 * @returns {JSX.Element|string} Role labels or "-" when empty.
 */
export const ReportRolesCell = ({ roles = [] }) => {
  const [expanded, setExpanded] = useState(false);

  if (!Array.isArray(roles) || roles.length === 0) {
    return "-";
  }

  const visibleRoles = expanded || roles.length <= 2 ? roles : roles.slice(0, 2);
  const showToggle = roles.length > 2;

  return (
    <div className="d-flex flex-column">
      {visibleRoles.map((role) => (
        <span key={`${role.value}-${role.label}`} className="mb-1">
          {RenderUserRole(role.label).html}
        </span>
      ))}
      {showToggle ? (
        <span
          className="table-expand-btn"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "See Less" : "See More"}
        </span>
      ) : null}
    </div>
  );
};

/**
 * Formats assigned users for CSV/XLSX/PDF export.
 * @param {Array<Object>} users - Users from the API.
 * @returns {string} Comma-separated user labels.
 */
export const formatReportUsersForExport = (users = []) => {
  if (!Array.isArray(users) || users.length === 0) {
    return "-";
  }

  return users
    .map((user) => {
      if (user.label) {
        return user.label;
      }

      const fullName = [user.Firstname, user.Lastname].filter(Boolean).join(" ").trim();
      return fullName || "-";
    })
    .join(", ");
};

/**
 * Formats assigned roles for CSV/XLSX/PDF export.
 * @param {Array<Object>} roles - Roles from the API.
 * @returns {string} Comma-separated role labels.
 */
export const formatReportRolesForExport = (roles = []) => {
  if (!Array.isArray(roles) || roles.length === 0) {
    return "-";
  }

  return roles.map((role) => role.label || "-").join(", ");
};

export const RenderReportStatus = (statusValue) => {
  const normalizedValue = statusValue?.toString() || "";

  switch (normalizedValue) {
    case "1":
      return {
        text: "Active",
        html: <p className="as-green-bg as-widget m-0">ACTIVE</p>,
      };

    case "0":
      return {
        text: "Draft",
        html: <p className="as-red-bg as-widget m-0">DRAFT</p>,
      };

    default:
      return {
        text: statusValue || "-",
        html: <p className="as-black-bg as-widget m-0">{statusValue || "-"}</p>,
      };
  }
};
